import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Spinner,
  Form,
  Container,
  Row,
  Col,
  Button,
} from "react-bootstrap";
import { FaSyncAlt } from "react-icons/fa";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

const ConvergenceOne = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);

  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluka, setSelectedTaluka] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("Convergence");

  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  /* -----------------------------------------------
      ⭐ HANDLE REPORT CHANGE
  ------------------------------------------------ */
  const handleFilterChange = (event) => {
    const selected = event.target.value;
    setSelectedFilter(selected);
    if (selected === "Convergence") navigate("/convergence");
    else if (selected === "ConvergenceOne") navigate("/convergenceOne");
  };

  /* -----------------------------------------------------------
      1️⃣ LOAD INITIAL DATA (ALL DISTRICTS)
  ----------------------------------------------------------- */
  const fetchInitialData = useCallback(async () => {
    if (!apiBaseUrl) {
      console.warn("apiBaseUrl not set");
      return;
    }
    setLoading(true);
    try {
      const url = `${apiBaseUrl}/convergenceReport/districts`;
      console.log("Fetching districts from:", url);
      const res = await fetch(url);
      const json = await res.json();
      console.log("Districts response:", json);
      if (Array.isArray(json)) {
        setData(json);
        setDistrictList(json);
      } else {
        console.error("API response is not an array:", json);
        setData([]);
        setDistrictList([]);
      }
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setData([]);
      setDistrictList([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  /* -----------------------------------------------------------
      2️⃣ WHEN DISTRICT SELECTED → GET TALUKAS
  ----------------------------------------------------------- */
  const fetchTalukasByDistrict = useCallback(
    async (districtId) => {
      if (!districtId) return;
      setLoading(true);
      try {
        const url = `${apiBaseUrl}/convergenceReport/districts/${districtId}/talukas`;
        console.log("Fetching talukas from:", url);
        const res = await fetch(url);
        const json = await res.json();
        console.log("Talukas response:", json);
        if (Array.isArray(json)) {
          setTalukaList(json);
          setData(json);
        } else {
          console.error("API response is not an array:", json);
          setTalukaList([]);
          setData([]);
        }
        setSelectedTaluka("");
      } catch (err) {
        console.error("Error fetching talukas:", err);
        setTalukaList([]);
        setData([]);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl]
  );

  /* -----------------------------------------------------------
      3️⃣ WHEN TALUKA SELECTED → FILTER ONLY THAT TALUKA
  ----------------------------------------------------------- */
  useEffect(() => {
    if (selectedTaluka && talukaList.length > 0) {
      const filtered = talukaList.filter(
        (t) => t.taluka_id === Number(selectedTaluka)
      );
      setData(filtered);
    }
  }, [selectedTaluka, talukaList]);

  /* -----------------------------------------------------------
      4️⃣ REFRESH ALL
  ----------------------------------------------------------- */
  const handleRefresh = () => {
    setSelectedDistrict("");
    setSelectedTaluka("");
    setTalukaList([]);
    setSearchTerm("");
    fetchInitialData();
  };

  /* -----------------------------------------------------------
      5️⃣ SEARCH FILTER
  ----------------------------------------------------------- */
  const filteredView = data.filter((item) =>
    Object.values(item).join(" ").toLowerCase().includes(searchTerm.toLowerCase())
  );

  /* -----------------------------------------------------------
      6️⃣ CALCULATE TOTALS
  ----------------------------------------------------------- */
  const calculateTotals = (items) => {
    let totals = {
      dalitVastiSudhar: 0,
      takkarBappa: 0,
      jilhaParishad: 0,
      panchayatSamiti: 0,
      grampanchayatSamiti: 0,
      csrNidhi: 0,
    };
    items.forEach((item) => {
      totals.dalitVastiSudhar += parseInt(item.tbl_dalit_vasti_sudhar || 0);
      totals.takkarBappa += parseInt(item.tbl_takkar_bappa || 0);
      totals.jilhaParishad += parseInt(item.tbl_jilha_parishad || 0);
      totals.panchayatSamiti += parseInt(item.tbl_panchayat_samiti || 0);
      totals.grampanchayatSamiti += parseInt(item.tbl_grampanchayat_samiti || 0);
      totals.csrNidhi += parseInt(item.tbl_crs_nidhi || 0);
    });
    return totals;
  };

  /* -----------------------------------------------------------
      7️⃣ EXPORT EXCEL
  ----------------------------------------------------------- */
  const exportToExcel = () => {
    const areaColumnName = selectedDistrict ? "तालुका" : "जिल्हा";

    const exportData = filteredView.map((item, index) => ({
      "अ. क्र": index + 1,
      [areaColumnName]: selectedDistrict ? item.taluka : item.district,
      "अनुसूचित जाती व नवबौध्द घटकांच्या वस्तीचा विकास करणे(ग्रामिण)":
        item.tbl_dalit_vasti_sudhar || "0",
      "ठक्कर बाप्पा": item.tbl_takkar_bappa || "0",
      "जिह्वा परिषद सेस": item.tbl_jilha_parishad || "0",
      "पंचायत समिती सेस": item.tbl_panchayat_samiti || "0",
      "ग्रामपंचायत सेस": item.tbl_grampanchayat_samiti || "0",
      "सी एस आर निधी": item.tbl_crs_nidhi || "0",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ConvergenceOne_Report");
    XLSX.writeFile(wb, "ConvergenceOne_Report.xlsx");
  };

  const totals = calculateTotals(filteredView);

  /* -----------------------------------------------------------
      LOAD INITIAL DATA
  ----------------------------------------------------------- */
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  /* -----------------------------------------------------------
      TRIGGER FETCH WHEN FILTERS CHANGE
  ----------------------------------------------------------- */
  useEffect(() => {
    if (selectedDistrict) fetchTalukasByDistrict(selectedDistrict);
  }, [selectedDistrict, fetchTalukasByDistrict]);

  return (
    <Container fluid>
      <Row>
        {/* LEFT FILTER PANEL */}
        <Col md={2} className="p-4">
          <Button variant="secondary" onClick={() => navigate("/")} className="mb-3 w-100">
            Back
          </Button>

          <div className="p-3 shadow-sm rounded mt-3" style={{ backgroundColor: "#EAF0F9" }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 className="fw-bold" style={{ color: "#1F3C88" }}>Filters</h5>
              <div
                onClick={handleRefresh}
                style={{
                  backgroundColor: "#EAF2FF",
                  borderRadius: "50%",
                  padding: "6px",
                  cursor: "pointer",
                }}
              >
                <FaSyncAlt size={16} style={{ color: "#1F3C88" }} />
              </div>
            </div>

            {/* Select Report */}
            <Form.Group controlId="filterSelect" className="mb-3">
              <Form.Label>Select Report</Form.Label>
              <Form.Control as="select" value={selectedFilter} onChange={handleFilterChange}>
                <option value="Convergence">State Scheme</option>
                <option value="ConvergenceOne">PMAY-G</option>
              </Form.Control>
            </Form.Group>

            <Form>
              {/* District */}
              <Form.Group className="mb-3">
                <Form.Label>जिल्हा</Form.Label>
                <Form.Select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                >
                  <option value="">Select District</option>
                  {Array.isArray(districtList) && districtList.map((d) => (
                    <option key={d.district_id} value={d.district_id}>
                      {d.district}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {/* Taluka */}
              <Form.Group className="mb-3">
                <Form.Label>तालुका</Form.Label>
                <Form.Select
                  value={selectedTaluka}
                  onChange={(e) => setSelectedTaluka(e.target.value)}
                  disabled={!talukaList.length}
                >
                  <option value="">Select Taluka</option>
                  {Array.isArray(talukaList) && talukaList.map((t) => (
                    <option key={t.taluka_id} value={t.taluka_id}>
                      {t.taluka}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </div>
        </Col>

        {/* MAIN TABLE */}
        <Col md={10} className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
            <h5 className="fw-bold mb-0" style={{ color: "#1F3C88" }}>
              प्रधानमंत्री आवास योजना-ग्रामीण अंतर्गत कृतीसंगमातून लाभ दिलेल्या लाभार्थ्यांची संख्या
            </h5>

            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: "250px" }}
              />
              <Button
                onClick={exportToExcel}
                style={{ backgroundColor: "#4da6ff", border: "none", minWidth: "130px" }}
              >
                Export Excel
              </Button>
            </div>
          </div>

          {loading ? (
            <Spinner animation="border" className="d-block mx-auto" />
          ) : (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table striped bordered hover responsive>
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th className="th-class-ac">अ. क्र</th>
                    {!selectedDistrict ? <th>जिल्हा</th> : <th>तालुका</th>}
                    <th>अनुसूचित जाती व नवबौध्द घटकांच्या वस्तीचा विकास करणे(ग्रामिण)</th>
                    <th>ठक्कर बाप्पा</th>
                    <th>जिह्वा परिषद सेस</th>
                    <th>पंचायत समिती सेस</th>
                    <th>ग्रामपंचायत सेस</th>
                    <th>सी एस आर निधी</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredView.length > 0 ? (
                    filteredView.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        {!selectedDistrict && <td>{item.district}</td>}
                        {selectedDistrict && <td>{item.taluka}</td>}
                        <td>{item.tbl_dalit_vasti_sudhar || "0"}</td>
                        <td>{item.tbl_takkar_bappa || "0"}</td>
                        <td>{item.tbl_jilha_parishad || "0"}</td>
                        <td>{item.tbl_panchayat_samiti || "0"}</td>
                        <td>{item.tbl_grampanchayat_samiti || "0"}</td>
                        <td>{item.tbl_crs_nidhi || "0"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No Records Found
                      </td>
                    </tr>
                  )}

                  {/* ⭐ TOTAL ROW */}
                  <tr style={{ backgroundColor: "#eef3ff", fontWeight: "bold" }}>
                    <td colSpan={2} style={{ textAlign: "center" }}>Total</td>
                    <td>{totals.dalitVastiSudhar}</td>
                    <td>{totals.takkarBappa}</td>
                    <td>{totals.jilhaParishad}</td>
                    <td>{totals.panchayatSamiti}</td>
                    <td>{totals.grampanchayatSamiti}</td>
                    <td>{totals.csrNidhi}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ConvergenceOne;
